import { useState, useEffect, useRef } from 'react';
import { LoaderCircle, HelpCircle } from 'lucide-react';

interface ItemIconProps {
  clusterId: string;
  itemId: string;
  initialDisplayName?: string;
  className?: string;
  displayMode?: 'card' | 'inline' | 'icon';
}

const ATLAS_BASE_URL = 'https://atlas.playcdu.co';

const itemDataCache = new Map<string, { src: string; displayName: string | null }>();

export const ItemIcon = ({ clusterId, itemId, initialDisplayName, className, displayMode = 'card' }: ItemIconProps) => {
  const cacheKey = `${clusterId}-${itemId}`;
  const cachedData = itemDataCache.get(cacheKey);

  const [imgSrc, setImgSrc] = useState<string | null>(cachedData?.src || null);
  const [displayName, setDisplayName] = useState<string | null>(cachedData?.displayName || initialDisplayName || null);
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(!cachedData);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);


  useEffect(() => {
    if (!isIntersecting || itemDataCache.has(cacheKey)) {
      return;
    }
    let isMounted = true;

    const fetchItemData = async () => {
      if (!itemId || !itemId.includes(':')) {
        if (isMounted) {
          setError(true);
          setIsLoading(false);
        }
        return;
      }
      const [namespace, id] = itemId.split(':');

      const primaryUrl = `${ATLAS_BASE_URL}/img/${clusterId}/${namespace}/${id}/0`;
      const fallbackUrl = `${ATLAS_BASE_URL}/search/first/${namespace}/${id}`;

        const processAndCache = (url: string, headers: Headers) => {
        const fetchedDisplayName = headers.get('X-Display-Name');
        const decodedDisplayName = fetchedDisplayName ? decodeURIComponent(fetchedDisplayName).replace(/\+/g, ' ') : null;

        if (isMounted) {
          setImgSrc(url);
          if (!initialDisplayName) {
            setDisplayName(decodedDisplayName);
          }
          setIsLoading(false);
          itemDataCache.set(cacheKey, { src: url, displayName: initialDisplayName || decodedDisplayName });
        }
      };

      const handleFetch = async (url: string, isFallback = false) => {
        try {
          const response = await fetch(url);
          if (!isMounted) return;

          if (response.ok) {
            // This is the final URL after any redirects
            processAndCache(response.url, response.headers);
          } else if (!isFallback) {
            // If the primary URL fails, try the fallback
            handleFetch(fallbackUrl, true);
          } else {
            // If the fallback also fails
            setError(true);
            setIsLoading(false);
          }
        } catch (e) {
          if (!isMounted) return;
          if (!isFallback) {
            handleFetch(fallbackUrl, true);
          } else {
            setError(true);
            setIsLoading(false);
          }
        }
      };

      handleFetch(primaryUrl);
    };

    fetchItemData();

    return () => { isMounted = false; };
  }, [clusterId, itemId, initialDisplayName, isIntersecting, cacheKey]);

  const handleImageLoad = () => setIsLoading(false);
  const handleImageError = () => {
    setError(true);
    setIsLoading(false);
  };

  if (displayMode === 'icon') {
    return (
      <span ref={ref as React.RefObject<HTMLSpanElement>} className="inline-flex items-center justify-center" title={displayName || itemId}>
        {error ? (
          <HelpCircle className={`${className || 'w-6 h-6'} text-glass-400`} />
        ) : isLoading ? (
          <LoaderCircle className={`${className || 'w-6 h-6'} animate-spin text-glass-400`} />
        ) : imgSrc ? (
          <img
            src={imgSrc}
            alt={displayName || itemId}
            className={`${className || 'w-6 h-6'} object-contain`}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        ) : (
          <LoaderCircle className={`${className || 'w-6 h-6'} animate-spin text-glass-400`} />
        )}
      </span>
    );
  }

  if (displayMode === 'inline') {
    if (error) {
      return (
        <span ref={ref as React.RefObject<HTMLSpanElement>} className="inline-flex items-center gap-1.5" title={itemId}>
          <HelpCircle className="w-5 h-5 text-glass-400" />
          <span>{displayName || itemId}</span>
        </span>
      );
    }

    const labelContent = displayName || itemId;
    const showIdOnHover = displayName && displayName !== itemId;

    return (
      <span
        ref={ref as React.RefObject<HTMLSpanElement>}
        className="inline-flex items-center gap-1.5 min-w-0 max-w-full"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {isLoading && <LoaderCircle className="w-5 h-5 animate-spin text-glass-400" />}
        {imgSrc && (
          <img
            src={imgSrc}
            alt={labelContent}
            className={`${className || 'w-5 h-5 object-contain'} transition-opacity duration-200 ${isHovered && showIdOnHover ? 'opacity-60' : ''}`}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ display: isLoading ? 'none' : 'block' }}
          />
        )}
        <span
          className={`relative inline-block min-w-0 rounded px-1 py-0.5 transition-all duration-200 ${isHovered && showIdOnHover ? 'overflow-visible' : 'overflow-hidden'}`}
        >
          {showIdOnHover ? (
            <span className="relative inline-block min-h-[1.25em] align-top">
              <span className={`block truncate transition-opacity duration-200 ${isHovered ? 'opacity-0' : ''}`}>
                {displayName}
              </span>
              <span
                className={`absolute left-0 top-0 z-50 min-w-max max-w-[min(24rem,90vw)] font-mono text-xs text-accent-300/90 transition-opacity duration-200 ${isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                aria-hidden
              >
                <span className="block break-all whitespace-normal px-2 py-1 rounded-md shadow-popover bg-dark-900/95 border border-glass-200/15">
                  {itemId}
                </span>
              </span>
            </span>
          ) : (
            <span>{labelContent}</span>
          )}
        </span>
      </span>
    );
  }

  // Card Mode Logic
  const itemIdClasses = () => {
    const len = itemId.length;
    let fontSizeClass = 'text-xs'; // 12px
    if (len > 35) {
      fontSizeClass = 'text-[9px]';
    } else if (len > 25) {
      fontSizeClass = 'text-[10px]';
    }
    return `${fontSizeClass} text-glass-400 font-mono mt-1 w-full break-words`;
  };

  return (
    <div ref={ref} className="flex flex-col items-center justify-center text-center h-full">
      <div className={className || 'w-10 h-10 mb-2 flex items-center justify-center'}>
        {error ? (
          <HelpCircle className="w-8 h-8 text-glass-500" />
        ) : isLoading ? (
          <LoaderCircle className="w-6 h-6 animate-spin text-glass-400" />
        ) : null}
        {imgSrc && !error && (
          <img 
            src={imgSrc} 
            alt={displayName || itemId} 
            className="w-full h-full object-contain"
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ display: isLoading ? 'none' : 'block' }}
          />
        )}
      </div>
      <span className="text-sm font-medium text-white truncate w-full min-h-[1.25rem] block" title={displayName || itemId}>{displayName || <>&nbsp;</>}</span>
      <span className={itemIdClasses()} title={itemId}>{itemId}</span>
    </div>
  );
};
